import type { ClientPreferencesComponentInput, ComponentValueResponse } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPublicClientPreferencesValue,
  savePublicClientPreferences,
} from "@/lib/api/public-flow/client-preferences";
import { useSessionToken } from "@/lib/context/public-session-context";
import { useInvalidatePublicDynamicSync } from "@/lib/hooks/use-public-flow/use-portfolios";
import { publicFlowCompletionQueryKey } from "@/lib/hooks/use-public-flow/use-public-flow-completion";

export const publicClientPreferencesKeys = {
  value: ["publicFlow", "client-preferences", "flow-instance-value"] as const,
};

export function usePublicClientPreferencesValue() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: publicClientPreferencesKeys.value,
    queryFn: () => getPublicClientPreferencesValue(token),
    enabled: !!token,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

export function useSavePublicClientPreferences() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidateDynamicSync = useInvalidatePublicDynamicSync();
  const queryKey = publicClientPreferencesKeys.value;
  // Every save carries the full answers map (and regenerates dynamic portfolios), so
  // saves run one at a time in click order (`scope`) and only the newest may write the
  // cache — a superseded save's reply or rollback would wipe the newer answers. A queued
  // save counts as pending, as does the settling one inside its own callbacks, so `> 1`
  // means a newer save is waiting.
  const isSuperseded = () => queryClient.isMutating({ mutationKey: queryKey }) > 1;
  return useMutation({
    mutationKey: queryKey,
    scope: { id: "public-client-preferences" },
    mutationFn: (input: ClientPreferencesComponentInput) =>
      savePublicClientPreferences(token, input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<ComponentValueResponse>(queryKey);
      queryClient.setQueryData<ComponentValueResponse>(queryKey, (old) => ({
        inputData: JSON.stringify(input),
        calculatedData: old?.calculatedData ?? null,
      }));
      return { previousData };
    },
    onError: (_err, _input, context) => {
      if (context?.previousData !== undefined && !isSuperseded()) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: (result) => {
      if (result.success && result.inputData !== undefined) {
        if (!isSuperseded()) {
          queryClient.setQueryData(queryKey, {
            inputData: result.inputData,
            calculatedData: result.calculatedData,
          });
        }
        // Completion semantics — keep the public progress bar/markers live.
        queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
        // QP-185 — the save regenerated dynamic portfolios.
        invalidateDynamicSync(result.dynamicSync);
      }
    },
  });
}
